/* =====================================================
   Number Selector Component – Version 2.1 (Modified: July 31, 2025)
   Interactive buttons for selecting numbers (use once)
   Date Modified: 31 July 2025 | Time: Current
===================================================== */

import { memo, useCallback } from 'react'
import clsx from 'clsx'
import { useGameState } from '../hooks/useGameState'
import { isNumberAvailable } from '../utils/gameLogic'
import styles from './NumberSelector.module.css'

/**
 * Number selection component
 * Each number can only be used once per equation attempt
 */
function NumberSelector() {
  const { NUMBERS_SET, usedNumbers, placeValue } = useGameState()

  /**
   * Handle number button click
   */
  const handleNumberClick = useCallback((number) => {
    if (isNumberAvailable(number, usedNumbers)) {
      placeValue(number.toString(), 'number')
    }
  }, [usedNumbers, placeValue])

  return (
    <div className={styles.selectGroup}>
      <h4>Numbers (Use Once)</h4>
      <div className={styles.selectArea}>
        {NUMBERS_SET.map((number) => (
          <NumberButton
            key={number}
            number={number}
            isUsed={!isNumberAvailable(number, usedNumbers)}
            onClick={handleNumberClick}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Individual number button component
 * Memoized to prevent unnecessary re-renders
 */
const NumberButton = memo(function NumberButton({ number, isUsed, onClick }) {
  const handleClick = useCallback(() => {
    if (!isUsed) {
      onClick(number)
    }
  }, [number, isUsed, onClick])

  const buttonClasses = clsx(
    styles.select,
    styles.numberButton,
    {
      [styles.used]: isUsed,
    }
  )

  return (
    <button
      className={buttonClasses}
      onClick={handleClick}
      disabled={isUsed}
      data-value={number}
      data-type="number"
    >
      {number}
    </button>
  )
})

export default NumberSelector